import { ErrorBoundary } from '@/components/ErrorBoundary'
import { RouteSkeleton } from '@/components/ui'
import { Shell } from '@/features/shell/Shell'
import { useAppStore } from '@/store/appStore'
import { Suspense, lazy } from 'react'
import { Navigate, Outlet, createBrowserRouter, useLocation } from 'react-router'

const LoginPage = lazy(() => import('@/features/auth/LoginPage'))
const ChangePasswordPage = lazy(() => import('@/features/auth/ChangePasswordPage'))
const HomePage = lazy(() => import('@/features/home/HomePage'))
const AttendancePage = lazy(() => import('@/features/attendance/AttendancePage'))
const SummaryPage = lazy(() => import('@/features/summary/SummaryPage'))
const MePage = lazy(() => import('@/features/me/MePage'))
const MembersPage = lazy(() => import('@/features/members/MembersPage'))

/**
 * The single gate. Signed out → login only. Temporary password → change it
 * first. Everything else → the app.
 */
function Guard() {
  const status = useAppStore((s) => s.status)
  const mustChange = useAppStore((s) => s.session?.user.mustChangePassword ?? false)
  const { pathname } = useLocation()

  if (status === 'signedOut' && pathname !== '/login') return <Navigate to="/login" replace />
  if (status === 'signedIn') {
    if (mustChange && pathname !== '/change-password') {
      return <Navigate to="/change-password" replace />
    }
    if (!mustChange && (pathname === '/login' || pathname === '/change-password')) {
      return <Navigate to="/" replace />
    }
  }
  return (
    <ErrorBoundary resetKey={pathname}>
      <Suspense fallback={<RouteSkeleton />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  )
}

export const router = createBrowserRouter([
  {
    element: <Guard />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/change-password', element: <ChangePasswordPage /> },
      { path: '/meetings/:meetingId', element: <AttendancePage /> },
      { path: '/me/members', element: <MembersPage /> },
      {
        element: <Shell />,
        children: [
          { path: '/', element: <HomePage /> },
          { path: '/summary', element: <SummaryPage /> },
          { path: '/me', element: <MePage /> },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])
